import React, { useState } from "react";
import { Modal, Button, FormTextarea, FormSelect } from "@/components/ui";
import { MessageSquare, UserPlus } from "lucide-react";
import { useTelecallers } from "../hooks/useTelecallers";
import { showApiError } from "@/lib/toast";

/**
 * ContactStatusModal
 * MATCHES LEGACY UI EXACTLY (Ref: CallLeadStageModal.jsx)
 */
export const ContactStatusModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  pendingStatus, 
  stages 
}) => {
  const [note, setNote] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load telecallers if we are assigning
  const { filteredTelecallers } = useTelecallers();

  if (!pendingStatus) return null;

  const { customer, targetStatus } = pendingStatus;
  const stage = stages?.find(s => s.key === targetStatus);
  const stageLabel = stage?.label || targetStatus;
  const currentStatusLabel = stages?.find(s => s.key === customer.status)?.label || customer.status;

  const isAssigning = targetStatus === 'assigned';

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const extraData = {};
      if (isAssigning && assignedTo) {
        extraData.assignedTo = assignedTo;
        extraData.telecallerId = assignedTo;
      }
      
      await onConfirm({ note: note.trim(), ...extraData });
      setNote("");
      setAssignedTo("");
      onClose();
    } catch (err) {
      showApiError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title={`Status Update: ${customer.customerName || [customer.firstName, customer.lastName].filter(Boolean).join(" ") || "Contact"}`} 
      size="md"
    >
      <div className="space-y-5">
        {/* Header Section: Avatar & Transition */}
        <div className="flex items-center gap-3 pb-3 border-b border-slate-100">
          <div className="w-10 h-10 rounded-full bg-brand-blue/10 flex items-center justify-center font-bold text-brand-blue">
            {customer.customerName?.charAt(0) || "C"}
          </div>
          <div>
            <div className="text-sm font-bold text-brand-dark leading-none">{customer.customerName}</div>
            <div className="text-[10px] text-slate-400 font-medium uppercase tracking-tight mt-1.5">
              {currentStatusLabel} <span className="mx-0.5 text-slate-300">→</span> <span className="text-brand-blue font-black">{stageLabel}</span>
            </div>
          </div>
        </div>

        {/* Telecaller Selection (Only for Assigned status) */}
        {isAssigning && (
          <div className="p-4 bg-indigo-50 rounded-xl border border-indigo-100 flex flex-col gap-3">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-white text-indigo-600 rounded-lg shadow-sm">
                <UserPlus size={18} />
              </div>
              <div>
                <div className="text-xs font-bold text-indigo-900">Assign Telecaller</div>
                <div className="text-[10px] text-indigo-600 mt-0.5">
                  Select a telecaller to handle this customer.
                </div>
              </div>
            </div>
            <FormSelect
              value={assignedTo}
              onChange={(e) => setAssignedTo(e.target.value)}
              placeholder="-- Select Telecaller --"
              className="bg-white border-indigo-200"
            >
              {filteredTelecallers.map(t => (
                <option key={t._id} value={t._id}>{t.name} ({t.employeeId || 'No ID'})</option>
              ))}
            </FormSelect>
          </div>
        )}

        {/* Info Box: Add Stage Remark */}
        <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 flex items-start gap-3">
          <div className="p-2 bg-white text-brand-blue rounded-lg shadow-sm">
            <MessageSquare size={18} />
          </div>
          <div>
            <div className="text-xs font-bold text-slate-800">Add Stage Remark</div>
            <div className="text-[10px] text-slate-500 mt-0.5 leading-relaxed">
              Please capture a quick note about this transition. This will be saved specifically for the <span className="font-bold text-brand-blue">{stageLabel}</span> stage.
            </div>
          </div>
        </div>

        {/* Textarea Section */}
        <div>
          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-1.5 pl-1">Stage Note / Comment</label>
          <FormTextarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="What happened? Enter details here..."
            rows={5}
            autoFocus
            className="text-[13px]"
          />
        </div>

        {/* Footer Buttons: Action Left, Cancel Right */}
        <div className="flex gap-3 pt-2">
          <Button 
            variant="orange" 
            className="flex-1 font-bold text-xs uppercase tracking-wider h-[40px] shadow-lg shadow-brand-orange/20" 
            onClick={handleSubmit} 
            disabled={(isAssigning && !assignedTo) || isSubmitting}
            loading={isSubmitting}
          >
            Move to {stageLabel}
          </Button>
          <Button 
            variant="secondary" 
            className="px-8 font-bold text-xs uppercase tracking-wider h-[40px] text-slate-400 border-slate-200 shadow-none" 
            onClick={onClose} 
            disabled={isSubmitting}
          >
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
};
